// ============================================================================
// ASSET MAINTENANCE TAB COMPONENT
// Maintenance tracking for asset entities
// Date: January 20, 2025
// ============================================================================

'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Wrench, Calendar, CheckCircle, AlertCircle, Clock, Trash2, DollarSign, ShieldCheck, ShieldAlert, ClipboardCheck } from 'lucide-react';
import { authService } from '@/lib/authService';
import { useTheme } from '@/contexts/ThemeContext';
import { confirm } from '@/lib/notifications';

// ============================================================================
// TYPES
// ============================================================================

interface MaintenanceRecord {
  id: string;
  maintenance_type: 'preventive' | 'corrective' | 'inspection' | 'calibration' | 'repair' | 'compliance_check' | 'hydrostatic_test' | 'pressure_test' | 'certification' | 'restocking' | 'replacement';
  description: string;
  performed_at: string;
  next_due_date?: string;
  cost?: number;
  performed_by?: string;
  notes?: string;
  recorded_at: string;
  inspection_outcome?: 'pass' | 'fail' | 'conditional' | '';
  certificate_number?: string;
  certificate_expiry?: string;
}

interface AssetMaintenanceTabProps {
  workItemId: string;
  isActive: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getAuthToken = (): string | null => {
  return authService.getTokens()?.access_token || null;
};

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR'
  }).format(amount);
};

const getMaintenanceTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    'preventive': 'Preventive',
    'corrective': 'Corrective',
    'inspection': 'Inspection',
    'calibration': 'Calibration',
    'repair': 'Repair',
    'compliance_check': 'Compliance Check',
    'hydrostatic_test': 'Hydrostatic Test',
    'pressure_test': 'Pressure Test',
    'certification': 'Certification',
    'restocking': 'Restocking',
    'replacement': 'Replacement'
  };
  return labels[type] || type;
};

const getMaintenanceTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    'preventive': '#10B981',
    'corrective': '#F59E0B',
    'inspection': '#3B82F6',
    'calibration': '#8B5CF6',
    'repair': '#EF4444',
    'compliance_check': '#0EA5E9',
    'hydrostatic_test': '#06B6D4',
    'pressure_test': '#14B8A6',
    'certification': '#6366F1',
    'restocking': '#84CC16',
    'replacement': '#F97316'
  };
  return colors[type] || '#6B7280';
};

const isComplianceType = (type: string): boolean => {
  return ['compliance_check', 'hydrostatic_test', 'pressure_test', 'certification'].includes(type);
};

const getDaysUntil = (dateString: string): number => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = date.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AssetMaintenanceTab({ workItemId, isActive }: AssetMaintenanceTabProps) {
  const [records, setRecords] = useState<MaintenanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [newRecord, setNewRecord] = useState({
    maintenance_type: 'preventive' as MaintenanceRecord['maintenance_type'],
    description: '',
    performed_at: new Date().toISOString().split('T')[0],
    next_due_date: '',
    cost: '',
    performed_by: '',
    notes: '',
    inspection_outcome: '' as MaintenanceRecord['inspection_outcome'],
    certificate_number: '',
    certificate_expiry: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const hasLoadedRef = useRef(false);
  const { currentTheme } = useTheme();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/work-items/${workItemId}/maintenance?limit=50`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // API returns { data: { work_item_id, summary, records } }
          const data = result.data;
          setRecords(Array.isArray(data) ? data : Array.isArray(data?.records) ? data.records : []);
        }
      }
    } catch (error) {
      console.error('Error loading maintenance data:', error);
    } finally {
      setLoading(false);
    }
  }, [workItemId]);

  useEffect(() => {
    hasLoadedRef.current = false;
  }, [workItemId]);

  useEffect(() => {
    if (isActive && !hasLoadedRef.current) {
      loadData();
      hasLoadedRef.current = true;
    }
  }, [isActive, loadData]);

  const handleAddRecord = async () => {
    if (!newRecord.description) return;

    try {
      setSubmitting(true);
      const response = await fetch(`/api/v1/work-items/${workItemId}/maintenance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          maintenance_type: newRecord.maintenance_type,
          description: newRecord.description,
          performed_at: newRecord.performed_at,
          next_due_date: newRecord.next_due_date || null,
          cost: newRecord.cost ? parseFloat(newRecord.cost) : null,
          performed_by: newRecord.performed_by || null,
          notes: newRecord.notes || null,
          inspection_outcome: newRecord.inspection_outcome || null,
          certificate_number: newRecord.certificate_number || null,
          certificate_expiry: newRecord.certificate_expiry || null,
          recorded_at: new Date().toISOString()
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setRecords([result.data, ...records]);
          setNewRecord({
            maintenance_type: 'preventive',
            description: '',
            performed_at: new Date().toISOString().split('T')[0],
            next_due_date: '',
            cost: '',
            performed_by: '',
            notes: '',
            inspection_outcome: '',
            certificate_number: '',
            certificate_expiry: ''
          });
        }
      }
    } catch (error) {
      console.error('Error adding maintenance record:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirm({ message: 'Delete this maintenance record?', variant: 'danger' }))) return;

    try {
      const response = await fetch(`/api/v1/work-items/${workItemId}/maintenance?id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        setRecords(records.filter(r => r.id !== id));
      }
    } catch (error) {
      console.error('Error deleting record:', error);
    }
  };

  const isDark = currentTheme.mode === 'dark';

  // Find next due maintenance
  const nextDue = records
    .filter(r => r.next_due_date)
    .sort((a, b) => new Date(a.next_due_date!).getTime() - new Date(b.next_due_date!).getTime())[0];

  // Calculate total maintenance cost
  const totalCost = records.reduce((sum, r) => sum + (r.cost || 0), 0);

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-12 bg-gray-200 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Maintenance Summary */}
      {records.length > 0 && (
        <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Total Maintenance Records
              </div>
              <div className={`text-2xl font-bold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                {records.length}
              </div>
            </div>
            <div>
              <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Total Maintenance Cost
              </div>
              <div className={`text-2xl font-bold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                {formatCurrency(totalCost)}
              </div>
            </div>
            {nextDue && (
              <div>
                <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Next Service Due
                </div>
                <div className={`text-2xl font-bold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                  {formatDate(nextDue.next_due_date!)}
                </div>
                <div className={`text-sm mt-1 ${getDaysUntil(nextDue.next_due_date!) < 7 ? 'text-red-500' : isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {getDaysUntil(nextDue.next_due_date!) < 0 
                    ? `Overdue by ${Math.abs(getDaysUntil(nextDue.next_due_date!))} days` 
                    : `In ${getDaysUntil(nextDue.next_due_date!)} days`}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add New Maintenance Record */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <h4 className={`font-medium mb-3 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
          Add Maintenance Record
        </h4>
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Maintenance Type
              </label>
              <select
                value={newRecord.maintenance_type}
                onChange={(e) => setNewRecord({ ...newRecord, maintenance_type: e.target.value as MaintenanceRecord['maintenance_type'] })}
                className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              >
                <optgroup label="General Maintenance">
                  <option value="preventive">Preventive</option>
                  <option value="corrective">Corrective</option>
                  <option value="inspection">Inspection</option>
                  <option value="calibration">Calibration</option>
                  <option value="repair">Repair</option>
                  <option value="replacement">Replacement</option>
                </optgroup>
                <optgroup label="H&S Compliance">
                  <option value="compliance_check">Compliance Check</option>
                  <option value="hydrostatic_test">Hydrostatic Test</option>
                  <option value="pressure_test">Pressure Test</option>
                  <option value="certification">Certification / Recertification</option>
                  <option value="restocking">Restocking</option>
                </optgroup>
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Description *
              </label>
              <input
                type="text"
                value={newRecord.description}
                onChange={(e) => setNewRecord({ ...newRecord, description: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                placeholder="Brief description of work performed"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Performed Date
              </label>
              <input
                type="date"
                value={newRecord.performed_at}
                onChange={(e) => setNewRecord({ ...newRecord, performed_at: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Next Due Date
              </label>
              <input
                type="date"
                value={newRecord.next_due_date}
                onChange={(e) => setNewRecord({ ...newRecord, next_due_date: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Cost (ZAR)
              </label>
              <input
                type="number"
                step="0.01"
                value={newRecord.cost}
                onChange={(e) => setNewRecord({ ...newRecord, cost: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Performed By
              </label>
              <input
                type="text"
                value={newRecord.performed_by}
                onChange={(e) => setNewRecord({ ...newRecord, performed_by: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                placeholder="Technician name or company"
              />
            </div>
          </div>
          {/* Inspection Outcome & Certificate - shown for compliance types */}
          {isComplianceType(newRecord.maintenance_type) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Inspection Outcome
                </label>
                <select
                  value={newRecord.inspection_outcome || ''}
                  onChange={(e) => setNewRecord({ ...newRecord, inspection_outcome: e.target.value as MaintenanceRecord['inspection_outcome'] })}
                  className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                >
                  <option value="">Select outcome</option>
                  <option value="pass">Pass</option>
                  <option value="conditional">Conditional Pass</option>
                  <option value="fail">Fail</option>
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Certificate Number
                </label>
                <input
                  type="text"
                  value={newRecord.certificate_number}
                  onChange={(e) => setNewRecord({ ...newRecord, certificate_number: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg font-mono ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  placeholder="Cert #"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Certificate Expiry
                </label>
                <input
                  type="date"
                  value={newRecord.certificate_expiry}
                  onChange={(e) => setNewRecord({ ...newRecord, certificate_expiry: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>
            </div>
          )}
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Notes
            </label>
            <textarea
              value={newRecord.notes}
              onChange={(e) => setNewRecord({ ...newRecord, notes: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              rows={2}
              placeholder="Additional notes or observations..."
            />
          </div>
          <button
            onClick={handleAddRecord}
            disabled={submitting || !newRecord.description}
            className="w-full px-4 py-2 text-white rounded-lg disabled:opacity-50 hover:opacity-90 transition-colors"
            style={{ backgroundColor: currentTheme.colors.primary }}
          >
            {submitting ? 'Adding...' : 'Add Maintenance Record'}
          </button>
        </div>
      </div>

      {/* Maintenance History */}
      {records.length === 0 ? (
        <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          No maintenance records yet
        </div>
      ) : (
        <div className="space-y-3">
          <h4 className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
            Maintenance History
          </h4>
          {records.map((record) => (
            <div
              key={record.id}
              className={`p-4 rounded-lg border-l-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
              style={{ borderLeftColor: getMaintenanceTypeColor(record.maintenance_type) }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span 
                      className="px-3 py-1 rounded-full text-sm font-medium text-white"
                      style={{ backgroundColor: getMaintenanceTypeColor(record.maintenance_type) }}
                    >
                      {getMaintenanceTypeLabel(record.maintenance_type)}
                    </span>
                    {record.next_due_date && getDaysUntil(record.next_due_date) < 7 && (
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full font-medium flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Due Soon
                      </span>
                    )}
                  </div>
                  <div className={`font-semibold mb-2 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    {record.description}
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-2">
                    <div>
                      <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Performed:
                      </span>
                      <span className={`ml-2 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {formatDate(record.performed_at)}
                      </span>
                    </div>
                    {record.next_due_date && (
                      <div>
                        <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Next Due:
                        </span>
                        <span className={`ml-2 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                          {formatDate(record.next_due_date)}
                        </span>
                      </div>
                    )}
                    {record.cost && (
                      <div>
                        <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Cost:
                        </span>
                        <span className={`ml-2 font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                          {formatCurrency(record.cost)}
                        </span>
                      </div>
                    )}
                    {record.performed_by && (
                      <div>
                        <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          By:
                        </span>
                        <span className={`ml-2 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                          {record.performed_by}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Inspection outcome and certificate info */}
                  {record.inspection_outcome && (
                    <div className="flex items-center gap-2 mt-2">
                      {record.inspection_outcome === 'pass' ? (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Pass
                        </span>
                      ) : record.inspection_outcome === 'fail' ? (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> Fail
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full font-medium flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> Conditional
                        </span>
                      )}
                      {record.certificate_number && (
                        <span className={`text-xs font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Cert: {record.certificate_number}
                        </span>
                      )}
                      {record.certificate_expiry && (
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Expires: {formatDate(record.certificate_expiry)}
                        </span>
                      )}
                    </div>
                  )}
                  {record.notes && (
                    <div className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {record.notes}
                    </div>
                  )}
                  <div className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    Recorded {formatTimeAgo(record.recorded_at)}
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(record.id)}
                  className="text-red-600 hover:text-red-800 ml-4"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
